import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { makePageSpaceChecker } from '../pdfPageSpace';

interface TendenciasReportGeneratorProps {
  chartData: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
    }>;
  };
  registrosPorObra: Array<{
    obra: string;
    registros: any[];
    total: number;
    completados: number;
    pendientes: number;
  }>;
  fechaGeneracion: string;
  filtroFecha: string;
}

const TendenciasReportGenerator: React.FC<TendenciasReportGeneratorProps> = ({
  chartData,
  registrosPorObra,
  fechaGeneracion,
  filtroFecha
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generarReporteTendenciasPDF = async () => {
    setIsGenerating(true);
    
    try {
      const doc = new jsPDF();
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      
      // Función auxiliar para verificar espacio y agregar página si es necesario
      const checkPageSpace = makePageSpaceChecker(doc, pageHeight, margin, () => yPosition, (y) => { yPosition = y; });
      
      // Configuración del documento
      doc.setFont('helvetica');
      doc.setFontSize(20);
      
      // Título principal
      doc.text('Reporte de Tendencias - Registros de Limpieza', 20, 30);
      
      // Fecha de generación y filtro
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generado el: ${fechaGeneracion}`, 20, 45);
      doc.text(`Período analizado: ${filtroFecha}`, 20, 55);
      
      // Línea separadora
      doc.setDrawColor(200, 200, 200);
      doc.line(20, 65, 190, 65);
      
      let yPosition = 85;
      
      // Resumen Ejecutivo
      checkPageSpace(40);
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text('Resumen Ejecutivo de Tendencias', 20, yPosition);
      yPosition += 20;
      
      // Análisis de tendencias
      if (chartData && chartData.labels.length > 0) {
        const datos = chartData.datasets[0].data;
        const totalRegistros = datos.reduce((sum, valor) => sum + valor, 0);
        const promedio = totalRegistros / datos.length;
        const maximo = Math.max(...datos);
        const minimo = Math.min(...datos);
        const tendencia = datos[datos.length - 1] > datos[0] ? 'Ascendente' : 
                         datos[datos.length - 1] < datos[0] ? 'Descendente' : 'Estable';
        
        const resumenTendencias = [
          ['Total de Registros', totalRegistros.toString()],
          ['Promedio Diario', promedio.toFixed(1)],
          ['Máximo Diario', maximo.toString()],
          ['Mínimo Diario', minimo.toString()],
          ['Tendencia General', tendencia]
        ];
        
        checkPageSpace(resumenTendencias.length * 15 + 30);
        autoTable(doc, {
          startY: yPosition,
          head: [['Métrica', 'Valor']],
          body: resumenTendencias,
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246], textColor: 255 },
          styles: { fontSize: 10 },
          margin: { top: 20, bottom: 20 }
        });
        
        yPosition = (doc as any).lastAutoTable?.finalY + 20 || yPosition + (resumenTendencias.length * 15 + 50);
      }
      
      // Gráfico de tendencias detallado
      if (chartData && chartData.labels.length > 0) {
        checkPageSpace(40);
        doc.setFontSize(16);
        doc.text('Análisis Diario de Registros', 20, yPosition);
        yPosition += 20;
        
        const tendenciaData = chartData.labels.map((label, index) => {
          const valor = chartData.datasets[0].data[index];
          const cambio = index > 0 ? valor - chartData.datasets[0].data[index - 1] : 0;
          const cambioTexto = index > 0 ? 
            (cambio > 0 ? `+${cambio}` : cambio < 0 ? `${cambio}` : '0') : 'N/A';
          
          return [
            label,
            valor.toString(),
            cambioTexto,
            cambio > 0 ? 'Subida' : cambio < 0 ? 'Bajada' : 'Estable'
          ];
        });
        
        checkPageSpace(tendenciaData.length * 12 + 30);
        autoTable(doc, {
          startY: yPosition,
          head: [['Fecha', 'Registros', 'Cambio', 'Tendencia']],
          body: tendenciaData,
          theme: 'grid',
          headStyles: { fillColor: [147, 51, 234], textColor: 255 },
          styles: { fontSize: 9 },
          margin: { top: 20, bottom: 20 }
        });
        
        yPosition = (doc as any).lastAutoTable?.finalY + 20 || yPosition + (tendenciaData.length * 12 + 50);
      }
      
      // Análisis por Obra
      if (registrosPorObra && registrosPorObra.length > 0) {
        checkPageSpace(40);
        doc.setFontSize(16);
        doc.text('Análisis de Actividades por Obra', 20, yPosition);
        yPosition += 20;
        
        const obraData = registrosPorObra.map(grupo => {
          const porcentajeCompletado = grupo.total > 0 ? 
            ((grupo.completados / grupo.total) * 100).toFixed(1) : '0';
          
          return [
            grupo.obra,
            grupo.total.toString(),
            grupo.completados.toString(),
            grupo.pendientes.toString(),
            `${porcentajeCompletado}%`
          ];
        });
        
        checkPageSpace(obraData.length * 12 + 30);
        autoTable(doc, {
          startY: yPosition,
          head: [['Obra', 'Total', 'Completados', 'Pendientes', '% Completado']],
          body: obraData,
          theme: 'grid',
          headStyles: { fillColor: [245, 158, 11], textColor: 255 },
          styles: { fontSize: 9 },
          margin: { top: 20, bottom: 20 }
        });
        
        yPosition = (doc as any).lastAutoTable?.finalY + 20 || yPosition + (obraData.length * 12 + 50);
      }
      
      // Análisis de Patrones
      if (chartData && chartData.labels.length > 0) {
        checkPageSpace(40);
        doc.setFontSize(16);
        doc.text('Análisis de Patrones y Recomendaciones', 20, yPosition);
        yPosition += 20;
        
        const datos = chartData.datasets[0].data;
        const diasConMasRegistros = chartData.labels[datos.indexOf(Math.max(...datos))];
        const diasConMenosRegistros = chartData.labels[datos.indexOf(Math.min(...datos))];
        
        // Calcular variabilidad
        const promedio = datos.reduce((sum, valor) => sum + valor, 0) / datos.length;
        const variabilidad = datos.reduce((sum, valor) => sum + Math.pow(valor - promedio, 2), 0) / datos.length;
        const desviacionEstandar = Math.sqrt(variabilidad);
        const coeficienteVariacion = (desviacionEstandar / promedio) * 100;
        
        const patronesData = [
          ['Día con más registros', diasConMasRegistros],
          ['Día con menos registros', diasConMenosRegistros],
          ['Variabilidad (CV)', `${coeficienteVariacion.toFixed(1)}%`],
          ['Consistencia', coeficienteVariacion < 30 ? 'Alta' : coeficienteVariacion < 60 ? 'Media' : 'Baja']
        ];
        
        checkPageSpace(patronesData.length * 15 + 30);
        autoTable(doc, {
          startY: yPosition,
          head: [['Patrón', 'Valor']],
          body: patronesData,
          theme: 'grid',
          headStyles: { fillColor: [34, 197, 94], textColor: 255 },
          styles: { fontSize: 10 },
          margin: { top: 20, bottom: 20 }
        });
        
        yPosition = (doc as any).lastAutoTable?.finalY + 20 || yPosition + (patronesData.length * 15 + 50);
        
        // Recomendaciones
        checkPageSpace(80);
        doc.setFontSize(14);
        doc.text('Recomendaciones Basadas en Tendencias:', 20, yPosition);
        yPosition += 20;
        
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        
        if (coeficienteVariacion > 60) {
          checkPageSpace(15);
          doc.text('• Alta variabilidad detectada. Considerar estandarizar horarios de limpieza.', 20, yPosition);
          yPosition += 15;
        }
        
        if (datos[datos.length - 1] < promedio) {
          checkPageSpace(15);
          doc.text('• Tendencia descendente reciente. Revisar procesos y motivación del personal.', 20, yPosition);
          yPosition += 15;
        }
        
        if (datos[datos.length - 1] > promedio * 1.2) {
          checkPageSpace(15);
          doc.text('• Incremento significativo en registros. Evaluar si es sostenible.', 20, yPosition);
          yPosition += 15;
        }
        
        checkPageSpace(15);
        doc.text('• Monitorear continuamente las tendencias para identificar patrones estacionales.', 20, yPosition);
        yPosition += 15;
      }
      
      // Pie de página
      checkPageSpace(30);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Sistema de Control de Limpieza - ControlLimpio', 20, yPosition);
      yPosition += 5;
      doc.text('Reporte de Tendencias - Generado automáticamente', 20, yPosition);
      
      // Guardar el PDF
      const nombreArchivo = `Tendencias_Registros_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(nombreArchivo);
      
    } catch (error) {
      console.error('Error al generar el reporte de tendencias PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 shadow rounded-lg p-6 mb-6 border border-purple-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-purple-900">Reporte de Tendencias</h3>
          <p className="text-sm text-purple-700">
            Genera reportes especializados en PDF de las tendencias de registros del dashboard
          </p>
        </div>
        <button
          onClick={generarReporteTendenciasPDF}
          disabled={isGenerating || !chartData || chartData.labels.length === 0}
          className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
            isGenerating || !chartData || chartData.labels.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-purple-600 text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2'
          }`}
        >
          {isGenerating ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generando...
            </div>
          ) : (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Generar Reporte de Tendencias
            </div>
          )}
        </button>
      </div>
      
      {(!chartData || chartData.labels.length === 0) && (
        <div className="text-center py-4">
          <div className="text-purple-500 text-sm">
            No hay datos de tendencias disponibles para generar el reporte
          </div>
        </div>
      )}
      
      {chartData && chartData.labels.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div className="bg-purple-50 p-3 rounded-md border border-purple-200">
            <div className="font-medium text-purple-900">Análisis Diario</div>
            <div className="text-purple-700">✓ Tendencias por fecha</div>
          </div>
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <div className="font-medium text-blue-900">Patrones</div>
            <div className="text-blue-700">✓ Variabilidad y consistencia</div>
          </div>
          <div className="bg-green-50 p-3 rounded-md border border-green-200">
            <div className="font-medium text-green-900">Recomendaciones</div>
            <div className="text-green-700">✓ Sugerencias basadas en datos</div>
          </div>
          <div className="bg-orange-50 p-3 rounded-md border border-orange-200">
            <div className="font-medium text-orange-900">Análisis por Obra</div>
            <div className="text-orange-700">✓ Distribución de actividades</div>
          </div>
          <div className="bg-indigo-50 p-3 rounded-md border border-indigo-200">
            <div className="font-medium text-indigo-900">Métricas Avanzadas</div>
            <div className="text-indigo-700">✓ CV, desviación estándar</div>
          </div>
          <div className="bg-pink-50 p-3 rounded-md border border-pink-200">
            <div className="font-medium text-pink-900">Resumen Ejecutivo</div>
            <div className="text-pink-700">✓ Vista general de tendencias</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TendenciasReportGenerator;
